import type { AgGridEvent } from 'ag-grid-community';
import type { GridApi } from 'ag-grid-enterprise';
import type Big from 'big.js';
import { isEqual, isNil } from 'lodash-es';
import { useEffect } from 'react';
import { iif, of } from 'rxjs';
import type { RefRate } from '../../../contexts';
import { useDynamicCallback, useNamedState, useObservableValue, useRefRates } from '../../../hooks';
import { useWatchlistVisibility } from '../../../providers';
import { EMPTY_ARRAY } from '../../../utils/empty';
import type { WatchListColumnTypes, WatchListRowData, WatchListRowNode } from '../types';
import { useRenderedRows } from './useRenderedRows';

const REQUIRED_COLUMNS: WatchListColumnTypes[] = ['refRate'];

// Returns undefined when the result matches currentRefRate, so callers can skip the grid update.
export function computeRefRateUpdate(
  currentRefRate: WatchListRowData['refRate'],
  sparklineClose: Big | undefined,
  refRate: RefRate | undefined
): WatchListRowData['refRate'] | undefined {
  const baselinePrice = refRate?.prevPrice ?? currentRefRate?.currentPrice ?? currentRefRate?.prevPrice;
  const newRate = refRate?.currentPrice ?? sparklineClose ?? baselinePrice;

  let newRefRate: WatchListRowData['refRate'];
  if ((isNil(baselinePrice) && !isNil(newRate)) || (!isNil(baselinePrice) && !newRate?.eq(baselinePrice))) {
    newRefRate = {
      currentPrice: newRate,
      prevPrice: baselinePrice,
      isLoaded: refRate?.isLoaded ?? true,
    };
  } else if (refRate) {
    newRefRate = {
      currentPrice: baselinePrice,
      prevPrice: baselinePrice,
      isLoaded: refRate.isLoaded,
    };
  }

  return newRefRate && !isEqual(newRefRate, currentRefRate) ? newRefRate : undefined;
}

/**
 * A hook to subscribe to Reference Rates only for the rows that are currently rendered on a grid.
 * Grid row data will be updated when the ref-rate data is updated, and then an async update will
 * be triggered on those rows that were updated.
 *
 * @param gridApi Api object for the grid this hook belongs to
 */
export function useDynamicRefRateSubscription(
  gridApi: GridApi | null,
  enabledColumns: { type: WatchListColumnTypes }[]
) {
  const [renderedRows, setRenderedRows] = useNamedState<string[]>([], 'renderedRows');
  const loadDataForRenderedRows = useDynamicCallback((_event: AgGridEvent<unknown>, renderedRowIds: string[]) => {
    setRenderedRows(enabledColumns.some(c => REQUIRED_COLUMNS.includes(c.type)) ? renderedRowIds : EMPTY_ARRAY);
  });
  const refRatesSubscription = useRefRates(renderedRows);

  const isWatchlistVisible = useWatchlistVisibility();
  // Subscribe to refrates only when the watchlist is visible, due to the nature of useObservableValue, the
  // subscription will be automatically unsubscribed when the watchlist is hidden.
  const latestRefRates = useObservableValue(
    () => iif(() => isWatchlistVisible, refRatesSubscription, of(new Map<string, RefRate | undefined>())),
    [refRatesSubscription, isWatchlistVisible],
    new Map()
  );

  useEffect(() => {
    const updates: WatchListRowData[] = [];
    for (const [symbol, refRate] of latestRefRates) {
      const gridRow = gridApi?.getRowNode(symbol) as WatchListRowNode;
      if (!gridRow) {
        continue;
      }
      const newRefRate = computeRefRateUpdate(gridRow.data?.refRate, gridRow.data?.sparklineData?.Close, refRate);
      if (newRefRate) {
        gridRow.data.refRate = newRefRate;
        updates.push(gridRow.data);
      }
    }
    if (updates.length) {
      gridApi?.applyTransactionAsync({ update: updates });
    }
  }, [gridApi, latestRefRates]);

  useRenderedRows(gridApi, loadDataForRenderedRows);
}
